import { useId } from 'react';
import { camelCase } from 'es-toolkit';

import {
  Box,
  FieldSet,
  Heading,
  LabeledControl,
  Legend,
  Label,
  Paragraph,
  Popover,
  Radio,
} from '@zillow/constellation-10';

import type { FeeDictionaryItemDisplayDto } from '@zg-rentals/ts-rental-manager-api-client';

type Option = {
  value: string;
  label: string;
};
type Props = {
  value: string;
  onChange: (value: string) => void;
  enums: Record<string, FeeDictionaryItemDisplayDto> | null;
  disabled?: boolean;
  required?: boolean;
  label: string;
  tooltipContent?: string;
  orientation?: 'horizontal' | 'vertical';
};
const FeeRadioGroupField = ({
  onChange,
  enums,
  disabled,
  value,
  required,
  label,
  tooltipContent = '',
  orientation = 'vertical',
}: Props) => {
  const legendTextId = useId();
  const radioOptions = [] as Array<Option>;
  if (enums) {
    Object.keys(enums).forEach((key) => {
      const { displayName } = enums[key];
      radioOptions.push({
        value: key,
        label: displayName,
      });
    });
  }

  const legend = (
    <Legend>
      <span id={legendTextId}>{label}</span>
      {/* margin, not a Legend gap, so the ::after required/optional text keeps its own spacing */}
      {tooltipContent && (
        <Box css={{ order: 2, display: 'flex', alignItems: 'center', marginInlineStart: 'tighter' }}>
          <Popover header={<Heading level={2}>Fee level</Heading>} body={<Paragraph>{tooltipContent}</Paragraph>} />
        </Box>
      )}
    </Legend>
  );

  // FieldSet's base style pins its Legend child to display:block, so overriding the same key makes
  // the legend a flex row. That in turn makes the required/optional ::after a flex item, so `order`
  // keeps it beside the label instead of trailing the Popover trigger.
  const fieldSetProps = {
    disabled,
    required,
    optional: !required,
    legend,
    'aria-labelledby': legendTextId,
    css: { '& > .c11n_legend': { display: 'flex', alignItems: 'center', _after: { order: 1 } } } as const,
  };

  if (orientation === 'horizontal') {
    return (
      <FieldSet {...fieldSetProps}>
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {radioOptions.map((option, index) => (
            <Box key={index}>
              <LabeledControl
                key={index}
                label={<Label optional={!required}>{option.label}</Label>}
                onChange={() => onChange(option.value)}
                control={
                  <Radio
                    name={`LabeledControl-${label}`}
                    checked={option.value === value}
                    data-testid={`CostsAndFeesFields-${camelCase(option.label)}RadioButton`}
                  />
                }
              />
            </Box>
          ))}
        </Box>
      </FieldSet>
    );
  }

  return (
    <FieldSet {...fieldSetProps}>
      {radioOptions.map((option, index) => (
        <LabeledControl
          key={index}
          label={<Label optional={!required}>{option.label}</Label>}
          onChange={() => onChange(option.value)}
          control={
            <Radio
              name={`LabeledControl-${label}`}
              checked={option.value === value}
              data-testid={`CostsAndFeesFields-${camelCase(option.label)}RadioButton`}
            />
          }
        />
      ))}
    </FieldSet>
  );
};

export default FeeRadioGroupField;
